import * as React from "react";
import { cn } from "@/lib/utils";

export interface PrivateCollectionItemSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement> {}

/**
 * Loading skeleton for PrivateCollectionItem component
 *
 * Matches the PrivateCollectionItem horizontal split layout with animated pulse effect.
 * Used to show loading state while fetching owner's Trophy Room items.
 *
 * @example
 * ```tsx
 * <PrivateCollectionItemSkeleton />
 * ```
 */
export const PrivateCollectionItemSkeleton = React.forwardRef<
  HTMLDivElement,
  PrivateCollectionItemSkeletonProps
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      role="status"
      aria-label="Loading Trophy Room item"
      className={cn("flex gap-[1rem] items-start w-full animate-pulse", className)}
      {...props}
    >
      {/* Product Image Skeleton (Left Side) */}
      <div className="flex-1 aspect-square bg-gray-100 rounded-[0.3125rem] min-h-px min-w-px shrink-0" />

      {/* Product Details Skeleton (Right Side) */}
      <div className="flex flex-1 flex-col gap-[1rem] items-start min-h-px min-w-px shrink-0">
        {/* Brand Name Skeleton */}
        <div className="h-[1.25rem] bg-gray-100 rounded w-1/4" />

        {/* Product Name Skeleton (2-3 lines) */}
        <div className="flex flex-col gap-[0.5rem] w-full">
          <div className="h-[1.25rem] bg-gray-100 rounded w-full" />
          <div className="h-[1.25rem] bg-gray-100 rounded w-3/4" />
        </div>

        {/* Product Info Grid Skeleton */}
        <div className="flex flex-col gap-[1rem] items-start w-full">
          {/* NFC ID Skeleton */}
          <div className="flex flex-col gap-[0.5rem] w-full">
            <div className="h-[1.25rem] bg-gray-100 rounded w-1/6" />
            <div className="h-[1.25rem] bg-gray-100 rounded w-2/3" />
          </div>

          {/* Product Number Skeleton */}
          <div className="flex flex-col gap-[0.5rem] w-full">
            <div className="h-[1.25rem] bg-gray-100 rounded w-1/6" />
            <div className="h-[1.25rem] bg-gray-100 rounded w-1/4" />
          </div>

          {/* Size Skeleton */}
          <div className="flex flex-col gap-[0.5rem] w-full">
            <div className="h-[1.25rem] bg-gray-100 rounded w-1/6" />
            <div className="h-[1.25rem] bg-gray-100 rounded w-1/6" />
          </div>
        </div>

        {/* Actions Skeleton */}
        <div className="flex flex-col gap-[0.5rem] items-start w-full">
          {/* Unpair Button Skeleton */}
          <div className="h-[1.25rem] bg-gray-100 rounded w-24" />

          {/* Visibility Toggle Skeleton */}
          <div className="flex items-center gap-[0.5rem]">
            <div className="size-[2.28125rem] bg-gray-100 rounded-[2.28125rem]" />
            <div className="h-[1.25rem] bg-gray-100 rounded w-32" />
          </div>
        </div>
      </div>
    </div>
  );
});

PrivateCollectionItemSkeleton.displayName = "PrivateCollectionItemSkeleton";
